import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Cell, Button } from '@highlight/ui'
import { PencilLine } from 'lucide-react'
import {
  AnimatedHeight,
  ASK_USER_CARD_THEMES,
  AskUserBadge,
  AskUserDismissButton,
  GrowingTextarea,
  OPTION_ROW,
  OPTION_ROW_DASHED,
  OPTION_ROW_IDLE,
  OPTION_ROW_PRESSABLE,
  OPTION_ROW_SURFACE,
  OptionBadge,
  ResolvedAnswer,
  ROW_STAGGER,
  staggerDelay,
  StepProgress,
  useRowDragGuard,
  type AskUserCardProgress,
  type AskUserCardTheme,
  type AskUserCardVariant,
} from '../ask-user-card'
import { FadingScrollable } from '../../components/elements/FadingScrollable'
import { useSubmitToolResponse } from '../../hooks/useSubmitMessage'
import { useMessagesStore } from '../../store'
import {
  buildFeedbackDismissal,
  buildFeedbackSave,
  feedbackQuestionText,
  feedbackTeachingSubtitle,
  parseAskUserFeedbackAnswer,
} from './types'
import type { AskUserFeedbackAnswer, AskUserFeedbackBlank, AskUserFeedbackInput } from './types'

/** Kept as an alias so existing imports of this name keep working. */
export type AskUserFeedbackCardVariant = AskUserCardVariant

interface AskUserFeedbackCardProps {
  input: AskUserFeedbackInput
  /** Defaults to `feedback` — the AskUserFeedback tool is the optional pattern. */
  variant?: AskUserCardVariant
  /**
   * The tool call id — used to post the tool-result back to resume the turn in
   * the *foreground* (inline-chat) flow. Optional because the background
   * (actions-panel) flow resolves the card via a staged-action endpoint and
   * supplies its own `onSubmit`.
   */
  toolCallId?: string
  conversationId?: string
  /** Existing tool-result, if the card was already completed (e.g. on reload). */
  existingResult?: string | null
  /**
   * Submission override. When provided, the card calls this instead of posting
   * a tool-result — used by the background actions-panel flow. Must throw on
   * failure so the card stays submittable for retry.
   */
  onSubmit?: (answer: AskUserFeedbackAnswer) => Promise<void>
  /** Suppress the card's own icon + `about` header (the drawer promotes it). */
  hideHeader?: boolean
  /** Render without the outer `Cell` chrome so the contents sit in a host container. */
  bare?: boolean
  /**
   * Let the body scroll inside a height-constrained bare host while the footer
   * stays pinned. The host bounds the height; the card only yields to it.
   */
  scrollableBody?: boolean
  progress?: AskUserCardProgress
  onProgress?: (answer: AskUserFeedbackAnswer) => void
  /** Retires the card without answering; the footer offers it when set. */
  onDismiss?: () => void
}

/** Roughly three lines before the Other box starts scrolling instead of growing. */
const OTHER_MAX_HEIGHT_PX = 132

/**
 * How long the primary button refuses to submit after it advances a step.
 * Covers the OS double-click interval, so the click that changed the label
 * can't also fire the action the label now names.
 */
const ADVANCE_SETTLE_MS = 500

const BODY_CLASS = 'flex min-w-0 basis-full flex-col gap-3'

/**
 * One question and its options: suggestion rows (single-select; tapping the
 * selected row again clears it) and the dashed "Other…" row that becomes an
 * inline text box when picked. The pairing between a question and its answers
 * is the row list itself — nothing floats free of the question above it.
 */
function BlankQuestion({
  blank,
  value,
  otherActive,
  emphasizeQuestion,
  theme,
  onSelect,
  onOtherActivate,
  onOtherChange,
  onSubmitRequest,
}: {
  blank: AskUserFeedbackBlank
  value: string
  /** The Other row is the active answer, even while its text is still empty. */
  otherActive: boolean
  emphasizeQuestion: boolean
  theme: AskUserCardTheme
  onSelect: (value: string) => void
  onOtherActivate: () => void
  onOtherChange: (value: string) => void
  /** Enter in the Other box attempts advance/submit. */
  onSubmitRequest: () => void
}) {
  const suggestions = blank.suggestions ?? []
  // With no suggestions the free-text box IS the answer — showing a dashed
  // "Other…" first would be a click that reveals the only thing on offer.
  const showOtherBox = otherActive || suggestions.length === 0
  const otherFilled = value.trim().length > 0
  const otherRowRef = useRef<HTMLDivElement>(null)
  const dragGuard = useRowDragGuard()
  // Keyed remount per step: the question leads and each option row follows
  // 40ms behind, so advancing reads as the next question assembling rather than
  // content teleporting. Purely decorative — rows are interactive throughout.
  const stagger = theme.staggerRows ? ROW_STAGGER : undefined
  const delay = (index: number) => (theme.staggerRows ? staggerDelay(index) : undefined)

  // Focus lands in the Other box the moment the user picks the row, so picking
  // and typing is one motion. Deliberately not on mount for a suggestion-less
  // blank — the card must not steal focus from the chat input.
  useEffect(() => {
    if (otherActive) otherRowRef.current?.querySelector('textarea')?.focus()
  }, [otherActive])

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p
        className={twMerge(
          'text-foreground cursor-text select-text text-[13px] leading-normal',
          emphasizeQuestion ? 'font-medium' : 'font-normal',
          stagger,
        )}
      >
        {feedbackQuestionText(blank)}
      </p>

      <div role="group" aria-label={blank.header} className="flex flex-col gap-1.5">
        {suggestions.map((suggestion, index) => {
          const selected = !otherActive && value === suggestion
          return (
            <button
              key={suggestion}
              type="button"
              // Tapping the active option clears it, so a mis-tap is undoable.
              onPointerDown={dragGuard.onPointerDown}
              onClick={(event) => {
                if (!dragGuard.wasSelection(event)) onSelect(selected ? '' : suggestion)
              }}
              aria-pressed={selected}
              style={delay(index + 1)}
              className={twMerge(
                OPTION_ROW,
                OPTION_ROW_SURFACE,
                OPTION_ROW_PRESSABLE,
                stagger,
                selected ? `border-border ${theme.accentSelected}` : OPTION_ROW_IDLE,
              )}
            >
              <OptionBadge letter={String.fromCharCode(65 + index)} selected={selected} theme={theme} />
              <span className="text-foreground min-w-0 flex-1 cursor-text select-text text-[13px] leading-normal">
                {suggestion}
              </span>
            </button>
          )
        })}

        {showOtherBox ? (
          // Staggers in only when it mounts with the question (no suggestions).
          // When the user activates it, it replaces the dashed row in place —
          // replaying a delayed entrance there would read as lag, not motion.
          <div
            ref={otherRowRef}
            style={otherActive ? undefined : delay(1)}
            className={twMerge(
              OPTION_ROW,
              OPTION_ROW_SURFACE,
              !otherActive && stagger,
              otherFilled ? `border-border ${theme.accentSelected}` : `border-border ${theme.otherFocusBorder}`,
            )}
          >
            <PencilLine aria-hidden="true" className="text-foreground-secondary mt-px size-3.5 shrink-0" />
            <GrowingTextarea
              rows={1}
              maxHeight={OTHER_MAX_HEIGHT_PX}
              value={value}
              onChange={(e) => onOtherChange(e.target.value)}
              onSubmitRequest={onSubmitRequest}
              aria-label={blank.header}
              placeholder={blank.placeholder ?? 'Type your answer…'}
              className="placeholder:text-foreground-placeholder text-foreground min-w-0 flex-1 resize-none bg-transparent text-[13px] leading-normal outline-none"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={onOtherActivate}
            style={delay(suggestions.length + 1)}
            className={twMerge(OPTION_ROW, OPTION_ROW_PRESSABLE, stagger, OPTION_ROW_DASHED)}
          >
            <PencilLine aria-hidden="true" className="text-foreground-secondary mt-px size-3.5 shrink-0" />
            <span className="text-foreground-secondary min-w-0 text-[13px] leading-normal">Other…</span>
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * A resolved question: the answer as one compact settled row (shared with the
 * output view), labelled by its sentence so the resolved card preserves the
 * prompt the user answered. The `about` header is context, not the question
 * itself. A skipped question keeps its row so the card still accounts for
 * everything it asked.
 */
function ResolvedQuestion({
  blank,
  value,
  emphasizeQuestion,
}: {
  blank: AskUserFeedbackBlank
  value: string
  emphasizeQuestion: boolean
}) {
  const filled = value.trim()
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <p
        className={twMerge(
          'text-foreground cursor-text select-text text-[13px] leading-normal',
          emphasizeQuestion ? 'font-medium' : 'font-normal',
        )}
      >
        {feedbackQuestionText(blank)}
      </p>
      <ResolvedAnswer answers={filled ? [filled] : []} emptyLabel="Skipped" />
    </div>
  )
}

export function AskUserFeedbackCard({
  input,
  variant = 'feedback',
  toolCallId,
  conversationId,
  existingResult,
  onSubmit,
  hideHeader,
  bare,
  scrollableBody = false,
  progress,
  onProgress,
  onDismiss,
}: AskUserFeedbackCardProps) {
  const submitToolResponse = useSubmitToolResponse()
  const { updateToolCall } = useMessagesStore()
  const config = ASK_USER_CARD_THEMES[variant]

  // Renders interactive when the result isn't our shape.
  const resolvedAnswer = useMemo(() => parseAskUserFeedbackAnswer(existingResult), [existingResult])
  const [submittedAnswer, setSubmittedAnswer] = useState<AskUserFeedbackAnswer | null>(resolvedAnswer)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // One question at a time (Figma "Question 1 of 3") — the card lives beside
  // Brief content in the sidepanel and cannot stack three option lists.
  const [stepIndex, setStepIndex] = useState(0)
  const [maxVisitedStepIndex, setMaxVisitedStepIndex] = useState(0)
  /** When the card last moved to a new question — see `ADVANCE_SETTLE_MS`. */
  const lastAdvanceAt = useRef(0)
  const [otherActive, setOtherActive] = useState<Record<string, boolean>>({})

  const isResolved = submittedAnswer !== null
  const wasDismissed = submittedAnswer?.dismissed === true

  const buildInitialState = useCallback(() => {
    const state: Record<string, string> = {}
    for (const blank of input.blanks) {
      state[blank.header] = resolvedAnswer?.responses.find((r) => r.header === blank.header)?.filled ?? ''
    }
    return state
  }, [input.blanks, resolvedAnswer])

  const [values, setValues] = useState(buildInitialState)

  // A tool-result can arrive after mount (the turn resolves while this card is
  // already rendered). Sync the local answered state so the card becomes
  // read-only and a second submit is impossible — but only when the card was
  // not already answered locally, so an in-progress edit isn't clobbered.
  useEffect(() => {
    if (resolvedAnswer && !submittedAnswer) {
      setSubmittedAnswer(resolvedAnswer)
      setValues(buildInitialState())
    }
  }, [resolvedAnswer, submittedAnswer, buildInitialState])

  // Resolve the card with what was actually posted. The resolved view renders
  // from `values`, and a dismissal posts every blank empty — leaving the typed
  // drafts in place would show checked answers under a "nothing saved" footer.
  const applyAnswer = useCallback(
    (answer: AskUserFeedbackAnswer) => {
      setSubmittedAnswer(answer)
      const state: Record<string, string> = {}
      for (const blank of input.blanks) {
        state[blank.header] = answer.responses.find((r) => r.header === blank.header)?.filled ?? ''
      }
      setValues(state)
    },
    [input.blanks],
  )

  const submit = useCallback(
    async (answer: AskUserFeedbackAnswer) => {
      if (isSubmitting || isResolved) return

      setIsSubmitting(true)
      setSubmitError(null)

      try {
        if (onSubmit) {
          // Background (actions-panel) flow. It must throw on failure so the
          // card stays submittable for retry.
          await onSubmit(answer)
          applyAnswer(answer)
        } else {
          if (!conversationId || !toolCallId) {
            console.error('AskUserFeedback: missing conversationId/toolCallId, cannot submit')
            setSubmitError('Failed to send that. Please try again.')
            return
          }
          const content = JSON.stringify(answer)
          // The helper does NOT throw on a non-OK POST — it returns the
          // Response — so inspect `response.ok` to detect failure.
          const response = await submitToolResponse(conversationId, toolCallId, content)
          if (!response.ok) {
            throw new Error(`Failed to submit feedback (status ${response.status})`)
          }
          applyAnswer(answer)
          updateToolCall(conversationId, toolCallId, { status: 'success', result: content })
        }
      } catch (error) {
        console.error('AskUserFeedback: failed to submit:', error)
        setSubmitError('Failed to send that. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    },
    [isSubmitting, isResolved, onSubmit, conversationId, toolCallId, submitToolResponse, updateToolCall, applyAnswer],
  )

  const totalSteps = input.blanks.length
  const isLastStep = stepIndex >= totalSteps - 1
  const activeBlank = input.blanks[Math.min(stepIndex, totalSteps - 1)]
  const progressTotal = progress?.total ?? totalSteps
  const progressIndex = (progress?.offset ?? 0) + stepIndex
  const isMulti = progressTotal > 1
  const showProgress = config.showProgress && isMulti && !isResolved
  const hasContextTitle = (input.about?.trim().length ?? 0) > 0
  const activeAnswered = (values[activeBlank.header] ?? '').trim().length > 0

  const handleSave = useCallback(() => {
    // Returns null when nothing was filled in — Enter in the Other box routes
    // here too, so the gate cannot live on the primary button alone. Saving
    // nothing is what Skip is for, and that path marks itself `dismissed`.
    const answer = buildFeedbackSave(input.blanks, values)
    if (answer) void submit(answer)
  }, [submit, input.blanks, values])

  /** Enter and the primary button share this: advance mid-card, save at the end. */
  const handleAdvance = useCallback(() => {
    if (!activeAnswered) return
    if (isLastStep) {
      // The same control turns into Save the instant the last question
      // renders, so a double-click on Next — or a held Enter — would submit a
      // question the user never saw, storing it empty. Ignore a save that
      // lands before the new step has been on screen long enough to read.
      if (performance.now() - lastAdvanceAt.current < ADVANCE_SETTLE_MS) return
      handleSave()
    } else {
      lastAdvanceAt.current = performance.now()
      const nextIndex = stepIndex + 1
      setMaxVisitedStepIndex((visitedIndex) => Math.max(visitedIndex, nextIndex))
      setStepIndex(nextIndex)
    }
  }, [activeAnswered, isLastStep, handleSave, stepIndex])

  useEffect(() => {
    if (!onProgress || maxVisitedStepIndex === 0 || isResolved) {
      return
    }
    const responses = input.blanks.map((blank) => ({
      header: blank.header,
      filled: (values[blank.header] ?? '').trim(),
    }))
    onProgress({ responses: responses.slice(0, maxVisitedStepIndex) })
  }, [input.blanks, isResolved, maxVisitedStepIndex, onProgress, values])

  /**
   * Skip is scoped to the question on screen, not the card: it clears this
   * step's answer and advances. On the last step it resolves the card with
   * whatever the earlier steps filled in — dismissing outright only when every
   * question was skipped, so skipping Q2 can't discard the Q1 answer.
   */
  const handleSkip = useCallback(() => {
    const cleared = { ...values, [activeBlank.header]: '' }
    setValues(cleared)
    setOtherActive((prev) => ({ ...prev, [activeBlank.header]: false }))
    if (!isLastStep) {
      lastAdvanceAt.current = performance.now()
      const nextIndex = stepIndex + 1
      setMaxVisitedStepIndex((visitedIndex) => Math.max(visitedIndex, nextIndex))
      setStepIndex(nextIndex)
      return
    }
    // Same settle guard as the primary button: a double-click on a mid-card
    // Skip must not also fire the last step's resolving Skip.
    if (performance.now() - lastAdvanceAt.current < ADVANCE_SETTLE_MS) return
    const answer = buildFeedbackSave(input.blanks, cleared)
    void submit(answer ?? buildFeedbackDismissal(input.blanks))
  }, [values, activeBlank.header, isLastStep, stepIndex, input.blanks, submit])

  // Only when the card draws its own header: the drawer promotes `about` to
  // its own header (see `getFeedbackFraming`) and would otherwise say it twice.
  const header = !hideHeader && (
    <Cell.Header showChevron={false}>
      <div className="flex min-w-0 items-center gap-2">
        {/* Bare cards read top-down as bar → question → subtext, with no icon. */}
        {!bare && <AskUserBadge variant={variant} />}
        <Cell.Title className="text-foreground line-clamp-none cursor-text select-text text-[13px] font-medium">
          {input.about}
        </Cell.Title>
      </div>
    </Cell.Header>
  )

  // AnimatedHeight carries every content reflow — step changes, the Other box
  // growing, the resolved summary landing — so the card resizes instead of
  // jumping between heights.
  const bodyContent = (
    <>
      {showProgress && (
        <StepProgress
          total={progressTotal}
          current={progressIndex}
          theme={config}
          onSelect={(index) => {
            const localIndex = index - (progress?.offset ?? 0)
            if (localIndex >= 0 && localIndex <= maxVisitedStepIndex) {
              setStepIndex(localIndex)
            }
          }}
          isStepEnabled={(index) => {
            const localIndex = index - (progress?.offset ?? 0)
            return localIndex >= 0 && localIndex <= maxVisitedStepIndex
          }}
        />
      )}
      {/* Title and the question below it sit tighter (8px) than the 12px stack. */}
      <div className={twMerge('flex min-w-0 flex-col gap-2', bare && !hideHeader && !showProgress && 'pt-1')}>
        {bare && header}
        <AnimatedHeight>
          {isResolved ? (
            // The settled answers fade in when the card resolves under the user,
            // so the open form doesn't hard-cut into its summary.
            <div className="animate-in fade-in flex min-w-0 flex-col gap-3 duration-200">
              {input.blanks.map((blank) => (
                <ResolvedQuestion
                  key={blank.header}
                  blank={blank}
                  value={values[blank.header]}
                  emphasizeQuestion={!hasContextTitle}
                />
              ))}
              {/* The one line the resolved card keeps: what became of the
                  answer. Naming the preference is what makes it legible as a
                  setting the user owns rather than feedback disappearing into
                  a form. Says "learns", never "always". */}
              <p className="text-foreground-placeholder text-[11px] leading-normal">
                {wasDismissed ? 'Skipped — nothing saved' : 'Saved to your preferences — Highlight learns from this'}
              </p>
            </div>
          ) : (
            <div className="flex min-w-0 flex-col gap-3">
              <BlankQuestion
                key={activeBlank.header}
                blank={activeBlank}
                value={values[activeBlank.header]}
                otherActive={otherActive[activeBlank.header] === true}
                emphasizeQuestion={!hasContextTitle}
                theme={config}
                onSelect={(value) => {
                  setValues((prev) => ({ ...prev, [activeBlank.header]: value }))
                  setOtherActive((prev) => ({ ...prev, [activeBlank.header]: false }))
                }}
                onOtherActivate={() => {
                  setValues((prev) => ({ ...prev, [activeBlank.header]: '' }))
                  setOtherActive((prev) => ({ ...prev, [activeBlank.header]: true }))
                }}
                onOtherChange={(value) => setValues((prev) => ({ ...prev, [activeBlank.header]: value }))}
                onSubmitRequest={handleAdvance}
              />
            </div>
          )}
        </AnimatedHeight>
      </div>
    </>
  )

  // The padding keeps the rows' borders clear of the scroll box edge and the
  // resting mask.
  const body = scrollableBody ? (
    <FadingScrollable className={twMerge(BODY_CLASS, '-mx-0.5 min-h-0 px-0.5 py-0.5')}>{bodyContent}</FadingScrollable>
  ) : (
    <div className={BODY_CLASS}>{bodyContent}</div>
  )

  // The line under the options (Figma 8695:22988): why answering is worth it
  // while the card is open. Once resolved the footer goes away — the settled
  // rows carry their own "saved to your preferences" line, matching the output
  // view's compact treatment.
  const statusLine = submitError ?? feedbackTeachingSubtitle()

  const footer = !isResolved && (
    <Cell.Footer>
      <div className="flex w-full flex-col gap-2">
        <span
          className={twMerge(
            'text-foreground-placeholder text-[13px] leading-normal',
            submitError && 'text-danger-subtle',
          )}
        >
          {statusLine}
        </span>
        <div className="flex w-full items-center justify-between gap-1.5">
          <div className="flex min-w-0 items-center gap-1.5">
            {onDismiss && <AskUserDismissButton onSelect={onDismiss} />}
            <span className="text-foreground-placeholder text-[13px]">
              {isMulti ? `Question ${progressIndex + 1} of ${progressTotal}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={handleSkip} disabled={isSubmitting}>
              Skip
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleAdvance}
              // Only greys out the affordance — `handleAdvance` holds the
              // invariant. Leaving a question unanswered is what Skip is
              // for, so Next/Save stay off until this one has an answer.
              disabled={isSubmitting || !activeAnswered}
              loading={isSubmitting}
              className={config.primaryButton}
            >
              {isLastStep && !progress?.hasFollowingSteps ? config.submitLabel : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </Cell.Footer>
  )

  if (bare) {
    return (
      <div className="flex min-h-0 flex-col gap-3">
        {body}
        {footer}
      </div>
    )
  }

  return (
    <Cell>
      {header}
      {body}
      {footer}
    </Cell>
  )
}
